/**
 * The two unit systems a user can pick, and the conversions between them.
 *
 * Values are stored canonically — metres, litres, °C, km/h — so a metric user sees the stored
 * number back unchanged and an imperial user sees it converted. That is the same contract the
 * dashboard's `useImperialValues` implements; the arithmetic lives here so mobile and web cannot
 * drift apart on what the same reading means.
 *
 * The factors are written out rather than taken from `convert-units`, which only the web apps
 * depend on. They are pinned in the tests against the exact strings `useImperialValues` already
 * produces, so a divergence between the two would fail rather than ship.
 */

export const UNIT_SYSTEMS = {
  METRIC: 'metric',
  IMPERIAL: 'imperial',
} as const;

export type UnitSystem = (typeof UNIT_SYSTEMS)[keyof typeof UNIT_SYSTEMS];

const FEET_PER_METER = 1 / 0.3048;

const KILOMETERS_PER_HOUR_PER_KNOT = 1.852;

export const metersToFeet = (meters: number): number => meters * FEET_PER_METER;

export const kilometersPerHourToKnots = (kilometersPerHour: number): number =>
  kilometersPerHour / KILOMETERS_PER_HOUR_PER_KNOT;

export const celsiusToFahrenheit = (celsius: number): number => celsius * (9 / 5) + 32;

/**
 * Joins a number to its unit the way the app already does it: word units read better spaced off
 * the number, a bare `°` sits tight against it. Same predicate as the dashboard's log table, which
 * is where the rule was settled — `°C` counts as a word unit because of the letter, so it is
 * spaced, and that is what the design draws too.
 */
export const withUnit = (value: string, unit: string): string =>
  /[a-z]/i.test(unit) ? `${value} ${unit}` : `${value}${unit}`;
